/**
 * CatalogHealthPage — admin-only "Saved & Remembered" product list.
 *
 * Surfaces every product created via Quick Add → Save & Remember
 * (quick_added=true), with quick filters, breakdown charts, and
 * a bulk-delete action. Helps admins audit catalog growth and clean
 * up duplicates / placeholder rates submitted by staff in a hurry.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { api, formatINR, formatISTDate } from '../lib/store';
import { toast } from 'sonner';
import { Sparkles, Trash2, Search, FileText } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from 'recharts';

// Pie slices: the design tokens (gold, olive, ink, olive-dark, mute, gold-soft, sub, olive-tint).
const COLORS = ['#B8860B', '#8C9A5F', '#1C1A14', '#6F7C47', '#8B8578', '#D4A628', '#5E594C', '#E8EBD7'];

export default function CatalogHealthPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    setError('');
    api.getCatalogHealth()
      .then(r => setData(r.data))
      .catch(err => setError(err.response?.data?.detail || 'Failed to load catalog health'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    const s = search.trim().toLowerCase();
    return data.products.filter(p => {
      if (filterCat !== 'all' && (p.main_category || 'Others') !== filterCat) return false;
      if (!s) return true;
      const hay = `${p.brand_name || ''} ${p.product_code || ''} ${p.product_name || ''} ${p.subcategory || ''} ${p.species || ''}`.toLowerCase();
      return hay.includes(s);
    });
  }, [data, search, filterCat]);

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(p => p.id)));
  };

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const doBulkDelete = async () => {
    setBusy(true);
    try {
      const ids = Array.from(selected);
      const res = await api.bulkDeleteCatalogHealth(ids);
      toast.success(`Deleted ${res.data.deleted} products`);
      setSelected(new Set());
      setConfirmDelete(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBusy(false);
    }
  };

  const categories = useMemo(() => {
    if (!data) return [];
    const set = new Set(data.products.map(p => p.main_category || 'Others'));
    return ['all', ...Array.from(set).sort()];
  }, [data]);

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="catalog-health-page">
      <header>
        <h1 className="font-display text-[20px] font-bold text-heda-ink flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-heda-gold" /> Catalog Health — Saved & Remembered
        </h1>
        <p className="text-xs sm:text-sm text-heda-sub mt-0.5">
          Every product staff added on-the-fly via Quick Add → Save & Remember. Audit, clean up, and keep your catalog tidy.
        </p>
      </header>

      {loading && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
      )}

      {!loading && error && (
        <Card className="rounded-2xl bg-white shadow-none border-red-200"><CardContent className="pt-4 text-sm text-red-700">{error}</CardContent></Card>
      )}

      {!loading && !error && data && (
        <>
          {/* Summary stat row */}
          <div className="grid grid-cols-3 gap-3">
            <Card className="rounded-2xl bg-white shadow-none border-heda-line" data-testid="ch-total-card">
              <CardContent className="pt-4 pb-3 px-3 sm:px-4">
                <p className="text-[10px] uppercase tracking-wide text-heda-mute">Total Saved</p>
                <p className="text-2xl font-bold text-heda-olive-dark mt-1">{data.summary.total}</p>
              </CardContent>
            </Card>
            <Card className="rounded-2xl bg-white shadow-none border-heda-line" data-testid="ch-month-card">
              <CardContent className="pt-4 pb-3 px-3 sm:px-4">
                <p className="text-[10px] uppercase tracking-wide text-heda-mute">This Month</p>
                <p className="text-2xl font-bold text-heda-gold mt-1">{data.summary.this_month}</p>
              </CardContent>
            </Card>
            <Card className="rounded-2xl bg-white shadow-none border-heda-line" data-testid="ch-30d-card">
              <CardContent className="pt-4 pb-3 px-3 sm:px-4">
                <p className="text-[10px] uppercase tracking-wide text-heda-mute">Last 30 Days</p>
                <p className="text-2xl font-bold text-amber-700 mt-1">{data.summary.last_30d}</p>
              </CardContent>
            </Card>
          </div>

          {/* Breakdown charts */}
          {data.products.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="rounded-2xl bg-white shadow-none border-heda-line">
                <CardHeader className="pb-2"><CardTitle className="text-sm">By Category</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-44">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.by_category} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E8E1D0" />
                        <XAxis dataKey="category" tick={{ fontSize: 10 }} stroke="#8B8578" />
                        <YAxis tick={{ fontSize: 11 }} stroke="#8B8578" allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#B8860B" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              <Card className="rounded-2xl bg-white shadow-none border-heda-line">
                <CardHeader className="pb-2"><CardTitle className="text-sm">By Staff Member</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-44">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={data.by_user} dataKey="count" nameKey="name" outerRadius={70}>
                          {data.by_user.map((d, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: '11px' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filters + bulk actions */}
          <Card className="rounded-2xl bg-white shadow-none border-heda-line">
            <CardContent className="pt-4">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <div className="relative flex-1 min-w-[160px]">
                  <Search className="absolute left-2 top-2.5 w-3.5 h-3.5 text-heda-mute" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search brand, code, name…"
                    className="rounded-xl bg-heda-cream/50 pl-7 h-8 text-sm"
                    data-testid="ch-search-input"
                  />
                </div>
                <select
                  value={filterCat}
                  onChange={(e) => setFilterCat(e.target.value)}
                  className="h-8 text-xs border border-heda-line rounded-xl px-2 bg-white"
                  data-testid="ch-cat-select"
                >
                  {categories.map(c => <option key={c} value={c}>{c === 'all' ? 'All categories' : c}</option>)}
                </select>
                {selected.size > 0 && (
                  <Button
                    variant="destructive" size="sm"
                    onClick={() => setConfirmDelete(true)}
                    className="rounded-xl ml-auto"
                    data-testid="ch-bulk-delete-btn"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete {selected.size} selected
                  </Button>
                )}
              </div>

              {/* Table */}
              <div className="overflow-auto max-h-[60vh] border border-heda-line rounded-xl">
                <table className="w-full text-xs sm:text-sm" data-testid="ch-products-table">
                  <thead className="bg-heda-cream sticky top-0 z-10">
                    <tr>
                      <th className="py-2 px-2 w-8">
                        <input
                          type="checkbox"
                          checked={selected.size > 0 && selected.size === filtered.length}
                          onChange={toggleAll}
                          className="accent-red-700"
                          data-testid="ch-select-all"
                        />
                      </th>
                      <th className="text-left py-2 px-2 font-semibold text-heda-sub">Brand · Code</th>
                      <th className="text-left py-2 px-2 font-semibold text-heda-sub hidden sm:table-cell">Category</th>
                      <th className="text-left py-2 px-2 font-semibold text-heda-sub hidden sm:table-cell">Thickness · Size</th>
                      <th className="text-right py-2 px-2 font-semibold text-heda-sub">Rate</th>
                      <th className="text-left py-2 px-2 font-semibold text-heda-sub hidden md:table-cell">Saved by</th>
                      <th className="text-left py-2 px-2 font-semibold text-heda-sub hidden md:table-cell">When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr><td colSpan="7" className="py-8 text-center text-heda-mute" data-testid="ch-empty">
                        {data.products.length === 0
                          ? 'No quick-added products yet. They appear here as staff use Save & Remember.'
                          : 'No matches for current filter.'}
                      </td></tr>
                    ) : filtered.map(p => (
                      <tr key={p.id} className="border-t border-heda-line hover:bg-heda-cream">
                        <td className="py-2 px-2">
                          <input
                            type="checkbox"
                            checked={selected.has(p.id)}
                            onChange={() => toggle(p.id)}
                            className="accent-red-700"
                            data-testid={`ch-select-${p.id}`}
                          />
                        </td>
                        <td className="py-2 px-2">
                          <div className="flex flex-col">
                            <span className="font-medium text-heda-ink">{p.brand_name || '—'} · {p.product_code || '—'}</span>
                            {p.subcategory && <span className="text-[10px] text-heda-mute">{p.subcategory}</span>}
                          </div>
                        </td>
                        <td className="py-2 px-2 text-heda-sub hidden sm:table-cell">{p.main_category || 'Others'}</td>
                        <td className="py-2 px-2 text-heda-sub hidden sm:table-cell text-[11px]">
                          {p.thickness ? `${p.thickness} · ` : ''}{(p.available_sizes || []).join(', ') || '—'}
                        </td>
                        <td className="py-2 px-2 text-right font-mono-data text-heda-olive-dark">{formatINR(p.base_rate)}</td>
                        <td className="py-2 px-2 text-heda-sub hidden md:table-cell text-[11px]">{p.created_by_name || '—'}</td>
                        <td className="py-2 px-2 text-heda-sub hidden md:table-cell text-[11px]">{p.created_at ? formatISTDate(p.created_at) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filtered.length > 0 && (
                <p className="text-[10px] text-heda-mute mt-2 flex items-center gap-1">
                  <FileText className="w-3 h-3" /> Showing {filtered.length} of {data.products.length} quick-added products.
                </p>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="border-heda-line bg-white sm:rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} products?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes them from the catalog. Existing quotations that reference them will keep their snapshot — but the product won't appear in new searches.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doBulkDelete} disabled={busy} className="bg-red-700 hover:bg-red-800">
              {busy ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
